'use client';

import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SectionHeading } from '@/components/shared/SectionHeading';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import type { AmbassadorContent } from '@/lib/content-schema';

interface Props {
  content: AmbassadorContent['pillars'];
}

export function PillarsSection({ content }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section className="py-24 md:py-32 bg-zibarit-bone">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading title={content.sectionTitle} subtitle={content.sectionSubtitle} />

        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {content.items.map((pillar) => (
            <motion.div
              key={pillar.title}
              variants={fadeInUp}
              className="relative bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow min-h-[180px] flex flex-col"
            >
              <div
                className="absolute left-0 top-6 bottom-6 w-1 rounded-full"
                style={{ backgroundColor: pillar.color }}
              />

              <div className="pl-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${pillar.color}20` }}
                >
                  <div style={{ color: pillar.color }}>
                    {pillar.iconKey === 'target' && <TargetIcon />}
                    {pillar.iconKey === 'coins' && <CoinsIcon />}
                    {pillar.iconKey === 'lightning' && <LightningIcon />}
                  </div>
                </div>

                <h3 className="text-subtitle text-base text-zibarit-negro mb-2">
                  {pillar.title}
                </h3>
                <p className="text-body text-sm text-zibarit-marron-oscuro">
                  {pillar.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function TargetIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

function CoinsIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function LightningIcon() {
  return (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}
